import AppRouterProviders from "@/components/common/app-router-providers";
import Bundles from "@/pages/bundles/bundles";
import PricingTimeline from "@/pages/bundles/pricing-timeline";
import ChooseBundleLinkTool from "@/pages/bundles/choose-bundle-link-tool";
import BundleCreate from "@/pages/bundles/bundle-create";
import BundleDetails from "@/pages/bundles/bundle-details";
import BundleEdit from "@/pages/bundles/bundle-edit";
import Products from "@/pages/products/products";
import ProductConfig from "@/pages/products/product-config";
import CouplesPack from "@/pages/couples-pack/couples-pack";
import Coupons from "@/pages/coupons/coupon";
import Regions from "@/pages/regions/regions";
import RegionCloneWizard from "@/pages/regions/region-clone-wizard";
import RegionCreateWizard from "@/pages/regions/region-create-wizard";
import RegionDetails from "@/pages/regions/region-details";
import Forms from "@/pages/forms/forms";
import FormCreate from "@/pages/forms/form-create";
import FormDetails from "@/pages/forms/form-details";
import InternalUser from "@/pages/internal-user/internal-user";
import ManageAccess from "@/pages/internal-user/manage-access";
import { NotificationDetails } from "@/pages/notifications/notification-details";
import Notifications from "@/pages/notifications/notifications";
import Broadcast from "@/pages/notifications/broadcast";
import OtpLog from "@/pages/otp-log/otp-log";
import Professionals from "@/pages/professionals/professional";
import OurMedicalTeam, { OurMedicalTeamRedirect } from "@/pages/professionals/our-medical-team";
import Prescriptions from "@/pages/prescriptions/prescriptions";
import { SingleSessionView } from "@/pages/sessions/session-details";
import Sessions from "@/pages/sessions/sessions";
import Submissions from "@/pages/submissions/submissions";
import UserDetails from "@/pages/users/user-details";
import Users from "@/pages/users/users";
import UsersExport from "@/pages/users/export";
import DecoratorLinks from "@/pages/users/decorator-links";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom";
import Invoices from "@/pages/invoices/invoices";
import Emails from "@/pages/emails/emails";
import PendingRequests from "@/pages/pending-requests/pending-requests";
import DuplicateAccountsAudit from "@/pages/audit/duplicate-accounts";
import BundleDiscrepancyAudit from "@/pages/audit/bundle-discrepancy";
import PaymentPages from "@/pages/payment-pages/payment-pages";
import PaymentPageCreate from "@/pages/payment-pages/payment-page-create";
import UnmappedSessionsAudit from "@/pages/audit/unmapped-sessions";
import RegionLayout from "@/components/common/region-layout";
import { AreaLanding, AreaRoute } from "@/components/common/area-route";
import RegionEntryRedirect from "@/components/common/region-entry-redirect";
import Teams from "@/pages/supply-config/teams";
import TeamForm from "@/pages/supply-config/team-form";
import TeamDetails from "@/pages/supply-config/team-details";
import TeamCalendly from "@/pages/supply-config/team-calendly";
import ManagedEvents from "@/pages/supply-config/managed-events";
import ManagedEventForm from "@/pages/supply-config/managed-event-form";
import ManagedEventCreate from "@/pages/supply-config/managed-event-create";
import ManagedEventDetails from "@/pages/supply-config/managed-event-details";
import ManagedEventCalendly from "@/pages/supply-config/managed-event-calendly";
import EventTypes from "@/pages/supply-config/event-types";
import EventTypeForm from "@/pages/supply-config/event-type-form";
import EventTypeDetails from "@/pages/supply-config/event-type-details";
import EventTypeStaging from "@/pages/supply-config/event-type-staging";

export const useCreateAppRouter = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route element={<AppRouterProviders />}>
        <Route index element={<RegionEntryRedirect />} />
        <Route path=":region" element={<RegionLayout />}>
          <Route index element={<AreaLanding />} />

          {/* Every sidebar page is its own area, so each gets its own gate. The
              static paths (users/export) outrank users/:id in the router. */}
          <Route element={<AreaRoute area="users" />}>
            <Route path="users">
              <Route index element={<Users />} />
              <Route path=":id" element={<UserDetails />} />
            </Route>
          </Route>
          <Route element={<AreaRoute area="users_export" />}>
            <Route path="users/export" element={<UsersExport />} />
          </Route>
          <Route element={<AreaRoute area="users_decorator_links" />}>
            <Route path="users/decorator-links" element={<DecoratorLinks />} />
          </Route>
          <Route element={<AreaRoute area="users_pending_requests" />}>
            <Route path="pending-requests">
              <Route index element={<PendingRequests />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="sessions" />}>
            <Route path="sessions">
              <Route index element={<Sessions />} />
              <Route path=":id" element={<SingleSessionView />} />
            </Route>
            <Route path="couples-packs">
              <Route index element={<CouplesPack />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="prescriptions" />}>
            <Route path="prescriptions">
              <Route index element={<Prescriptions />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="bundles" />}>
            <Route path="bundles">
              <Route index element={<Bundles />} />
              <Route path="new" element={<BundleCreate />} />
              <Route path=":id" element={<BundleDetails />} />
              <Route path=":id/edit" element={<BundleEdit />} />
            </Route>
            <Route path="pricing" element={<PricingTimeline />} />
            <Route
              path="choose-bundle-link"
              element={<ChooseBundleLinkTool />}
            />
            <Route path="products">
              <Route index element={<Products />} />
              <Route path=":productId/config" element={<ProductConfig />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="payment_pages" />}>
            <Route path="payment-pages">
              <Route index element={<PaymentPages />} />
              <Route path="new" element={<PaymentPageCreate />} />
            </Route>
          </Route>

          {/* Supply Config: each page is its own area, so each gets its own gate. */}
          <Route element={<AreaRoute area="supply_config_professionals" />}>
            <Route path="professionals">
              <Route index element={<Professionals />} />
            </Route>
            <Route path="our-medical-team">
              <Route index element={<OurMedicalTeam />} />
              <Route path=":slug" element={<OurMedicalTeamRedirect />} />
            </Route>
          </Route>
          <Route element={<AreaRoute area="supply_config_teams" />}>
            <Route path="teams">
              <Route index element={<Teams />} />
              <Route path="calendly-groups" element={<TeamCalendly />} />
              <Route path="new" element={<TeamForm />} />
              <Route path=":id" element={<TeamDetails />} />
              <Route path=":id/edit" element={<TeamForm />} />
            </Route>
          </Route>
          <Route element={<AreaRoute area="supply_config_managed_events" />}>
            <Route path="managed-events">
              <Route index element={<ManagedEvents />} />
              <Route path="calendly-data" element={<ManagedEventCalendly />} />
              <Route path="new" element={<ManagedEventCreate />} />
              <Route path=":id" element={<ManagedEventDetails />} />
              <Route path=":id/edit" element={<ManagedEventForm />} />
            </Route>
          </Route>
          <Route element={<AreaRoute area="supply_config_event_types" />}>
            <Route path="event-types">
              <Route index element={<EventTypes />} />
              <Route path="raw-data" element={<EventTypeStaging />} />
              <Route path="new" element={<EventTypeForm />} />
              <Route path=":id" element={<EventTypeDetails />} />
              <Route path=":id/edit" element={<EventTypeForm />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="coupons" />}>
            <Route path="coupons">
              <Route index element={<Coupons />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="forms" />}>
            <Route path="forms">
              <Route index element={<Forms />} />
              <Route path="new" element={<FormCreate />} />
              <Route path=":id" element={<FormDetails />} />
            </Route>
          </Route>
          <Route element={<AreaRoute area="forms_submissions" />}>
            <Route path="submissions">
              <Route index element={<Submissions />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="notifications" />}>
            <Route path="notifications">
              <Route index element={<Notifications />} />
              <Route path=":id" element={<NotificationDetails />} />
            </Route>
          </Route>
          <Route element={<AreaRoute area="notifications_push" />}>
            <Route path="notifications/broadcast" element={<Broadcast />} />
          </Route>
          <Route element={<AreaRoute area="notifications_emails" />}>
            <Route path="emails">
              <Route index element={<Emails />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="invoices" />}>
            <Route path="invoices">
              <Route index element={<Invoices />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="otp_logs" />}>
            <Route path="otp-logs">
              <Route index element={<OtpLog />} />
            </Route>
          </Route>

          <Route element={<AreaRoute area="internal_users" />}>
            <Route path="admins">
              <Route index element={<InternalUser />} />
              <Route path="manage" element={<ManageAccess />} />
            </Route>
            {/* Region administration is config, not a page of its own — it sits
                with Internal Users rather than carrying its own grid row. */}
            <Route path="regions">
              <Route index element={<Regions />} />
              <Route path="new" element={<RegionCreateWizard />} />
              <Route path="clone" element={<RegionCloneWizard />} />
              <Route path=":id" element={<RegionDetails />} />
            </Route>
          </Route>

          {/* /audit only redirects; each audit page is gated on its own row, and
              a role without the first page is bounced on to what it can open. */}
          <Route path="audit" element={<Navigate to="duplicate-accounts" />} />
          <Route element={<AreaRoute area="audit_duplicate_accounts" />}>
            <Route
              path="audit/duplicate-accounts"
              element={<DuplicateAccountsAudit />}
            />
          </Route>
          <Route element={<AreaRoute area="audit_bundle_discrepancy" />}>
            <Route
              path="audit/bundle-discrepancy"
              element={<BundleDiscrepancyAudit />}
            />
          </Route>
          <Route element={<AreaRoute area="audit_unmapped_sessions" />}>
            <Route
              path="audit/unmapped-sessions"
              element={<UnmappedSessionsAudit />}
            />
          </Route>

          <Route path="*" element={<AreaLanding />} />
        </Route>
      </Route>,
    ),
  );
};
